import { Directive, Injector, Input, output } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { EMPTY, Observable } from 'rxjs';
import {
  ColDef,
  CellValueChangedEvent,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { FieldTypes } from '@digiwall/ag-grid';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ObserverQueue } from '@digiwall/core';
import { DocLineUpdatedDto, DocLineRole, CustomerDocDto } from '@digiwall/goliath-documents/proxy';
import { VatType } from '@digiwall/goliath-core/proxy';
import { makeDocLineProductionCol } from '../../../components';
import { ShapingService } from '../../../services';
import { LinePriceDetails } from '../../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';
import { BaseCustomerDocServiceForLineEditor } from './base-customer-doc-service-for-line-editor';
import { BaseThirdPartyDocLineEditor } from '../../../components';
import { DocType } from '../../base-docs/detail/doc-type';

@Directive()
export abstract class BaseCustomerDocLineEditor<
  TDoc extends CustomerDocDto,
> extends BaseThirdPartyDocLineEditor<TDoc> {
  @Input() set document(value: TDoc) {
    this._document = value as any;
  }
  @Input() set readonly(value: boolean) {
    this._readonly = value;
  }

  public lineUpdatedEvent = output<Partial<DocLineUpdatedDto>>();
  public linePricingDetailsEvent = output<LinePriceDetails>();

  protected articleLineRole: DocLineRole;
  protected textLineRole: DocLineRole;
  protected chapterLineRole: DocLineRole;
  protected handlingLinesUpdatedEvent = true;
  protected computationModuleActive = false;

  public columnDefs: Array<ColDef> = [];

  constructor(
    protected localizationService: LocalizationService,
    protected _docService: BaseCustomerDocServiceForLineEditor,
    protected shapingService: ShapingService,
    protected injector: Injector,
    protected _updateQueue: ObserverQueue,
    protected confirmationService: ConfirmationService,
  ) {
    super(localizationService, _docService, injector, _updateQueue);
  }

  protected initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        {
          field: 'isOptionnal',
          headerName: this.localizationService.instant('Doc::IsOptionnal'),
          headerTooltip: this.localizationService.instant('Doc::IsOptionnal:Tooltip'),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          sortable: false,
          editable: params => this.isCellEditable(params) && !this.isParentLineOptional(params),
          suppressMovable: true,
          pinned: 'right',
          lockPinned: true,
          width: 75,
        },
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Sales),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_NetUnitPrice,
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        makeDocLineProductionCol(
          k => this.localizationService.instant(k),
          e => this.isCellEditable(e),
          row => {
            this.computationModuleActive = true;
            this.openComputation(row);
          },
        ),
        {
          field: 'purchasePrice',
          width: 120,
          headerName: this.localizationService.instant('Doc::PurchasePrice'),
          type: FieldTypes.Currency,
          cellRendererParams: {
            decimals: () => this._document.currencyPrecision,
          },
          suppressNavigable: true,
          hide: true,
        },
        {
          field: 'purchasePriceMargin',
          width: 120,
          headerName: this.localizationService.instant('Doc::PurchasePriceMargin'),
          type: FieldTypes.Percentage,
          suppressNavigable: true,
          hide: true,
        },
        {
          field: 'totalPurchasePriceGain',
          width: 120,
          headerName: this.localizationService.instant('Doc::TotalPurchasePriceGain'),
          type: FieldTypes.Currency,
          cellRendererParams: {
            decimals: () => this._document.currencyPrecision,
          },
          suppressNavigable: true,
          hide: true,
        },
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole && params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }

    return isEditable && super.isCellEditable(params) && !this.computationModuleActive;
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    let menuItems = this.contextMenuItems(params);

    let line = params?.node?.data;

    if (line?.computationModuleEnable) {
      menuItems.push('separator', {
        name: this.localizationService.instant('Shaping::ComputationModule'),
        action: _ => {
          this.openComputation(line);
        },
      });
    }

    return menuItems;
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this._docService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
            true,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this._docService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this._docService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
            true,
          );
          break;
        case 'unitWeight':
          observable = this._docService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (event: CellValueChangedEvent<any, any>): void => {
    const obs = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          const oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({ update: [oldData] });
        },
      });
  };

  openComputation = (line): void => {
    this.shapingService.openComputation({
      docId: this._document.id,
      concurrencyStamp: this._document.concurrencyStamp,
      thirdPartyId: this._document.thirdPartyId,
      line: line,
      docService: this._docService,
      docLanguageId: this._document.languageId,
      callbackFn: result => {
        this.computationModuleActive = false;
        if (result?.errors?.length > 0) {
          this.confirmationService
            .warn(
              result.errors.join('\n'),
              this.localizationService.instant('Shaping::ComputationModule:ErrorsOccurred'),
              { hideCancelBtn: true, yesText: this.localizationService.instant('::Ok') },
            )
            .subscribeOnce();
        }
        if (result) {
          this.removeChildren(line.id);
          this._updateLines(result);
        }
      },
    });
  };
}
